import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, Tag, Button, Space, Typography, Alert } from 'antd';
import { CheckOutlined, StopOutlined } from '@ant-design/icons';
import { listVaultPatients } from '../../actions/identifierVaultActions';
import OptOutModal, { RecordConsentModal } from '../identifierVault/OptOutModal.component';

const { Text } = Typography;

// Protocol §5.3 requires opt-out to be checked at screening and again at 1, 3 and 5 years, so the
// consent state has to be visible on the patient record itself, not only in the vault screen.
// The vault participant is found by Study Hash, which is unique and is the only vault key the
// tenant Patient row carries.
const PatientConsentStatus = ({ patientId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [optOutOpen, setOptOutOpen] = useState(false);
    const [consentOpen, setConsentOpen] = useState(false);

    // Read the record the detail screen already loaded rather than refetching it.
    const { patient } = useSelector((s) => s.patientDetails || {});
    const studyHash = patient?.id === patientId ? patient?.studyHash : null;
    const { vaultPatients = [], loading, error } = useSelector((s) => s.vaultPatientList || {});
    const participant = vaultPatients.find((v) => v.studyHash === studyHash);

    const reload = () => {
        if (studyHash) dispatch(listVaultPatients({ filter: 'studyHash', query: studyHash, page: 1, limit: 1 }));
    };

    useEffect(reload, [dispatch, studyHash]);

    if (!studyHash) {
        return (
            <Card size="small" title={t('Research consent')} style={{ marginTop: 24 }}>
                <Alert
                    type="info"
                    showIcon
                    message={t('No Study Hash yet')}
                    description={t('A Study Hash needs an NHS or CHI number plus a date of birth. Add them to link this patient to the identifier vault.')}
                />
            </Card>
        );
    }

    // A 403 here just means this user cannot read the vault; the patient record itself is fine.
    if (error) return null;

    const isWithdrawn = participant?.isOptedOut === true;

    return (
        <Card
            size="small"
            title={t('Research consent')}
            loading={loading}
            style={{ marginTop: 24 }}
            extra={
                isWithdrawn ? (
                    <Tag color="red">{t('Withdrawn')}{participant?.optOutSource ? ` — ${participant.optOutSource}` : ''}</Tag>
                ) : (
                    <Space>
                        <Tag color={participant?.isOptedIn ? 'green' : 'default'}>
                            {participant?.isOptedIn ? t('Opted in') : t('No recorded consent')}
                        </Tag>
                        {participant && !participant.isOptedIn && (
                            <Button size="small" icon={<CheckOutlined />} onClick={() => setConsentOpen(true)}>
                                {t('Record consent')}
                            </Button>
                        )}
                        {participant && (
                            <Button danger size="small" icon={<StopOutlined />} onClick={() => setOptOutOpen(true)}>
                                {t('Record withdrawal')}
                            </Button>
                        )}
                    </Space>
                )
            }
        >
            <Space direction="vertical" size={4}>
                <Text type="secondary">
                    {t('Study Hash')}:{' '}
                    <Text code copyable>
                        {studyHash}
                    </Text>
                </Text>
                {!participant?.isOptedIn && !isWithdrawn && (
                    <Text type="secondary">
                        {t('No recorded consent means this participant is in the identifier vault but research opt-in has not been set. Record it here, or on Identifier Vault → participant → Opted in.')}
                    </Text>
                )}
                {participant?.optInConsentDate && (
                    <Text type="secondary">
                        {t('Consent recorded')}: {new Date(participant.optInConsentDate).toLocaleDateString()}
                    </Text>
                )}
                {participant?.optOutRequestedAt && (
                    <Text type="secondary">
                        {t('Withdrawn on')}: {new Date(participant.optOutRequestedAt).toLocaleDateString()}
                    </Text>
                )}
            </Space>

            {participant && (
                <>
                    <RecordConsentModal
                        open={consentOpen}
                        onClose={() => setConsentOpen(false)}
                        participantId={participant.id}
                        participantLabel={studyHash}
                        onCompleted={reload}
                    />
                    <OptOutModal
                        open={optOutOpen}
                        onClose={() => setOptOutOpen(false)}
                        participantId={participant.id}
                        participantLabel={studyHash}
                        onCompleted={reload}
                    />
                </>
            )}
        </Card>
    );
};

export default PatientConsentStatus;
